import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RiskBadge } from "@/components/Badge";
import { Shield, CheckCircle, XCircle, Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface AdminReviewQueueProps {
  pendingMacros: any[];
  onRefresh: () => void;
}

export const AdminReviewQueue = ({ pendingMacros, onRefresh }: AdminReviewQueueProps) => {
  const { toast } = useToast();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedMacroId, setSelectedMacroId] = useState<string | null>(null);

  const handleApproveMacro = async (macroId: string) => {
    try {
      const { error } = await supabase.functions.invoke("approve-macro", {
        body: { macro_id: macroId, is_approved: true },
      });

      if (error) throw error;

      toast({ title: "Success", description: "Macro approved successfully" });
      onRefresh();
    } catch (error) {
      console.error("Error approving macro:", error);
      toast({
        title: "Error",
        description: "Failed to approve macro",
        variant: "destructive",
      });
    }
  };

  const handleRejectMacro = async () => {
    if (!selectedMacroId) return;

    try {
      const { error } = await supabase.functions.invoke("approve-macro", {
        body: { macro_id: selectedMacroId, is_approved: false },
      });

      if (error) throw error;

      toast({
        title: "Macro Rejected",
        description: "The macro has been rejected and removed from the queue",
      });
      onRefresh();
      setRejectDialogOpen(false);
      setSelectedMacroId(null);
    } catch (error) {
      console.error("Error rejecting macro:", error);
      toast({
        title: "Error",
        description: "Failed to reject macro",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <Card className="mb-6 bg-primary/5 border-primary/20">
        <CardHeader>
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6 text-primary" />
            <div>
              <CardTitle>Admin Review Queue</CardTitle>
              <CardDescription>
                {pendingMacros.length} macro{pendingMacros.length !== 1 ? "s" : ""} awaiting approval
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {pendingMacros.map((macro) => (
              <div
                key={macro.id}
                className="flex items-center justify-between p-4 border rounded-lg bg-background"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-base truncate">{macro.name}</h3>
                    <RiskBadge risk={macro.risk_score} />
                  </div>
                  <p className="text-sm text-muted-foreground mb-1">
                    by {macro.public_profiles?.username || "Unknown User"}
                    {macro.public_profiles?.verified && " ✓"}
                  </p>
                  <p className="text-sm line-clamp-1">{macro.short_desc}</p>
                  {macro.risk_flags && macro.risk_flags.length > 0 && (
                    <div className="mt-2 p-2 bg-destructive/10 rounded text-xs text-destructive">
                      ⚠️ {macro.risk_flags.length} security flag(s) detected
                    </div>
                  )}
                </div>
                <div className="flex gap-2 ml-4 shrink-0">
                  <Button size="sm" onClick={() => handleApproveMacro(macro.id)}>
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      setSelectedMacroId(macro.id);
                      setRejectDialogOpen(true);
                    }}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Reject
                  </Button>
                  <Button size="sm" variant="outline" asChild>
                    <Link to={`/m/${macro.slug}`}>
                      <Eye className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject Macro Submission?</AlertDialogTitle>
            <AlertDialogDescription>
              This will reject the macro submission. The author will need to resubmit if they want to
              publish this macro.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedMacroId(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRejectMacro}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
